import { memo, useCallback, useState } from 'react';
import { numberToLocale } from 'utils/translate';
import classes from 'classnames';
import HandleIcon from 'assets/icons/vertical-hamburger.svg?react';
import ReactSlider from 'react-slider';
import styles from './Slider.module.scss';

export type Props = {
  min: number;
  max: number;
  value?: number;
  heading?: string;
  hideHeaders?: boolean;
  step?: number;
  units?: string;
  onChange?: (value: any) => void;
  onAfterChange?: (value: any) => void;
  dataTest?: string;
  classNameLimits?: string;
  className?: string;
  disabled?: boolean;
};

type Thumb = {
  valueNow: number;
};

const Slider = memo(function Slider({
  min,
  max,
  value,
  heading,
  step,
  units,
  onChange,
  onAfterChange,
  dataTest,
  className,
  classNameLimits,
  hideHeaders = false,
  disabled = false,
}: Props) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const hideTooltip = useCallback(() => setIsTooltipVisible(false), []);

  const showTooltip = useCallback(() => {
    setIsTooltipVisible(true);
  }, []);

  const renderThumb = useCallback(
    (props: Object, state: Thumb) => {
      const { valueNow } = state;

      return (
        <div {...props}>
          <div
            // Desktop
            onMouseDown={showTooltip}
            onMouseUp={hideTooltip}
            // Mobile
            onTouchStart={showTooltip}
            onTouchEnd={hideTooltip}
            // Classname
            className={classes(
              styles.handle,
              disabled && styles['background-disabled'],
            )}
          >
            <HandleIcon className={styles['handle-icon']} />
            {isTooltipVisible && (
              <div className={classes(styles.tooltip)}>
                <div className={styles.amount}>
                  <span data-test="thumb" className={styles.value}>
                    {valueNow}
                  </span>
                  {units && <span className={styles.units}>{units}</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    },
    [showTooltip, isTooltipVisible, units],
  );

  return (
    <div data-test={dataTest} className={className}>
      {!hideHeaders && (
        <div className={styles.labels} data-test={`${dataTest}-labels`}>
          <span
            className={styles.heading}
            data-test={`${dataTest}-labels-heading`}
          >
            {heading}
          </span>
          <span
            className={styles['amount-label']}
            data-test={`${dataTest}-labels-amount`}
          >
            <span data-test={`${dataTest}-labels-amount-value`}>
              {numberToLocale(value, 0)}
            </span>
            {units && (
              <span
                className={styles.units}
                data-test={`${dataTest}-labels-amount-units`}
              >
                {' '}
                {units}
              </span>
            )}
          </span>
        </div>
      )}
      <ReactSlider
        className={classes(styles.slider, `${dataTest}-dataTestClassSlider`)}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        value={value}
        thumbClassName={classes(styles.thumb, `${dataTest}-dataTestClassThumb`)}
        trackClassName={classes(
          styles.track,
          disabled && styles['border-disabled'],
          `${dataTest}-dataTestClassTrack`,
        )}
        renderThumb={renderThumb}
        onChange={onChange}
        onAfterChange={onAfterChange}
      />
      <div
        className={classes(styles.limits, classNameLimits)}
        data-test={`${dataTest}-limits`}
      >
        <span data-test={`${dataTest}-limits-min`}>
          {min} {units}
        </span>
        <span data-test={`${dataTest}-limits-max`}>
          {numberToLocale(max, 0)} {units}
        </span>
      </div>
    </div>
  );
});

export default Slider;
